import React, { useState, useRef, useEffect } from 'react';
import { useFileUpload, FileUploadResult } from 'hooks/useFileUpload';
import s from './s.module.less';
import { Button, Spin } from 'antd';
import { uploadPrivateFileByProviderId } from 'api/common';
import { providerGetKlarityResourcesUrl } from 'api/provider';
import { getUserId } from 'utils/localstore';

interface AttachFilesProps {
    onFilesChange?: (files: FileUploadResult[]) => void;
    maxFiles?: number;
    accept?: string;
    maxSize?: number;
    value?: FileUploadResult[];
}

export const isImage = (file: FileUploadResult) => {
    return file.type ? file.type.startsWith('image/') : true;
};

export const truncateFileName = (fileName: string, maxLength: number = 20) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4);
    return `${truncatedName}...${extension}`;
};

const AttachFiles: React.FC<AttachFilesProps> = ({
    onFilesChange,
    maxFiles = 5,
    accept = '*',
    maxSize = 10,
    value,
}) => {
    const [files, setFiles] = useState<FileUploadResult[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const getUrlFromResource = async (resource: FileUploadResult) => {
        const res = await providerGetKlarityResourcesUrl(resource.url || '');
        if (!res.error) {
            return res.data?.data?.singedUrl || '';
        }
    };

    useEffect(() => {
        const fetchAttachmentUrls = async () => {
            if (value && value.length > 0 && files.length === 0) {
                setLoadingPreview(true);
                const newFiles = await Promise.all(
                    value.map(async (file) => {
                        const url = await getUrlFromResource(file);
                        return { ...file, previewUrl: url };
                    }),
                );
                setLoadingPreview(false);
                setFiles(newFiles);
            }
            if (value && value.length === 0) {
                setFiles([]);
            }
        };
        fetchAttachmentUrls();
    }, [value]);

    const { loading, uploadFile } = useFileUpload({
        maxSize,
        accept,
        onSuccess: (result) => {
            const newFiles = [...files, result];
            setFiles(newFiles);
            onFilesChange?.(newFiles);
        },
        uploadFn: uploadPrivateFileByProviderId,
        formData: {
            uploadType: 'TREATMENT_ATTACHMENT',
            providerId: getUserId()?.toString() ?? '',
        },
    });

    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles) return;

        // 检查文件数量限制
        if (files.length + selectedFiles.length > maxFiles) {
            alert(`You can only upload up to ${maxFiles} files`);
            return;
        }

        // 逐个上传文件
        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            await uploadFile(file);
        }

        // 清空input值，允许重复选择同一文件
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (index: number) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        onFilesChange?.(newFiles);
    };

    return (
        <div className={s.attachFilesContainer}>
            {/* 文件预览卡片 */}
            {files.length > 0 && (
                <Spin spinning={loadingPreview}>
                    <div className={s.filePreviewContainer}>
                        {files.map((file, index) => (
                            <div key={index} className={s.filePreviewCard}>
                                {isImage(file) ? (
                                    <div className={s.filePreview}>
                                        <img src={file.previewUrl} alt={file.name} className={s.filePreviewImage} />
                                    </div>
                                ) : (
                                    <div className={s.fileInfo}>
                                        <div className={s.fileName}>{file.name || ''}</div>
                                        <div className={s.fileType}>
                                            {file.type ? file.name?.split('.').pop()?.toUpperCase() : 'Unknown type'}
                                        </div>
                                    </div>
                                )}
                                <Button className={s.removeButton} onClick={() => handleRemoveFile(index)}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="25"
                                        height="25"
                                        viewBox="0 0 25 25"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M12.5004 24.1004C18.9069 24.1004 24.1004 18.9069 24.1004 12.5004C24.1004 6.09389 18.9069 0.900391 12.5004 0.900391C6.09389 0.900391 0.900391 6.09389 0.900391 12.5004C0.900391 18.9069 6.09389 24.1004 12.5004 24.1004ZM10.6257 8.57509C10.0594 8.00883 9.14135 8.00883 8.57509 8.57509C8.00883 9.14135 8.00883 10.0594 8.57509 10.6257L10.4498 12.5004L8.57509 14.3751C8.00883 14.9413 8.00883 15.8594 8.57509 16.4257C9.14135 16.992 10.0594 16.992 10.6257 16.4257L12.5004 14.551L14.3751 16.4257C14.9413 16.992 15.8594 16.992 16.4257 16.4257C16.992 15.8594 16.992 14.9413 16.4257 14.3751L14.551 12.5004L16.4257 10.6257C16.992 10.0594 16.992 9.14135 16.4257 8.57509C15.8594 8.00883 14.9413 8.00883 14.3751 8.57509L12.5004 10.4498L10.6257 8.57509Z"
                                            fill="#111827"
                                            fillOpacity="0.5"
                                        />
                                    </svg>
                                </Button>
                            </div>
                        ))}
                    </div>
                </Spin>
            )}

            {/* 隐藏的文件输入 */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={accept}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {/* Attach files 按钮 */}
            <div className={`${s.attachFiles} ${loading ? s.loading : ''}`} onClick={handleAttachClick}>
                <span className={s.iconBg}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="14" viewBox="0 0 9 14" fill="none">
                        <path
                            d="M3.14538 3.52869V8.96169C3.14538 9.60602 3.66771 10.1284 4.31204 10.1284C4.95637 10.1284 5.47871 9.60602 5.47871 8.96169L5.40794 3.59946C5.40794 2.3108 4.36327 1.26613 3.0746 1.26613C1.78594 1.26613 0.741271 2.3108 0.741271 3.59946L0.812042 8.96169C0.812042 10.8947 2.37905 12.4617 4.31204 12.4617C6.24504 12.4617 7.81204 10.8947 7.81204 8.96169L7.81811 3.8057"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                <span className={s.attachText}>{loading ? 'Uploading...' : 'Attach files'}</span>
            </div>
        </div>
    );
};

export default AttachFiles;
